import Styles from './AutopilotFreemiumUpgradeModal.module.scss'

import { Modal, useModals } from 'almanac-ui/Modal'
import { Button } from 'blaze-ui/Button'
import { Heading } from 'blaze-ui/Heading/Heading'
import { Send1, Help1, Gift01, ArrowRight, Copy } from 'blaze-ui/icons'
import XSquareContained from 'blaze-ui/icons/24/XSquareContained'
import { Text } from 'blaze-ui/Text/Text'
import { AutopilotFreemiumChannelSelectModal } from 'components/AutopilotFreemiumChannelSelectModal/AutopilotFreemiumChannelSelectModal'
import { useOpenAutopilotUpgrade } from 'hooks/useOpenAutopilotUpgrade'
import LockOpen3 from 'blaze-ui/icons/24/LockOpen3'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useCurrentBrandPlan } from 'hooks/queries/brandPlans/useCurrentBrandPlan'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { WorkspaceClient } from 'api/clients/WorkspaceClient'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { useReferralUrl } from 'components/Settings/ReferralModalV2/hooks/useReferralUrl'
import { ReferralModalV2 } from 'components/Settings/ReferralModalV2/ReferralModalV2'
import { useBrandPlanSchedules } from 'hooks/queries/schedules/useBrandPlanSchedules'
import { collectResults } from 'utils/query'
import dayjs from 'dayjs'
import { useUpdateBrandPlanSchedule } from 'hooks/queries/schedules/useUpdateBrandPlanSchedule'
import { useMemo } from 'react'
import { BrandPlanContents } from 'types'
import { buildLimitedChannels } from 'components/AutopilotFreemiumChannelSelectModal/utils'
import { FormValues } from 'components/AutopilotFreemiumChannelSelectModal/types'
import { trackFreemiumModalCompleted } from './trackFreemiumModalCompleted'
import { FREE_REWARD_TIERS } from 'hooks/useAutopilotLimits'

export const AutopilotFreemiumUpgradeModal = () => {
  const { openModal, closeModal, closeAllModals } = useModals()
  const openVirtualMarketerPlansModal = useOpenAutopilotUpgrade()
  const workspace = useCurrentWorkspace()
  const { data: brandPlan } = useCurrentBrandPlan()
  const schedulesQuery = useBrandPlanSchedules(
    {
      brandPlanId: brandPlan?.id ?? -1,
      start_date: dayjs().format('YYYY-MM-DD'),
    },
    { enabled: !!brandPlan },
  )
  const schedules = collectResults(schedulesQuery, (data) => data.schedules).filter(Boolean)
  const isFreemiumReferrals = useFlipperFlag('freemium_referrals')
  const { referralUrl } = useReferralUrl()

  const copyUrlToClipboard = () => {
    navigator.clipboard.writeText(referralUrl)
    showToastMessage('Copied to clipboard!', 'success')
  }

  const plannedSchedule = useMemo(
    () => schedules.find((schedule) => schedule?.scheduled_status === 'planned'),
    [schedules],
  )

  // Use mutation to update the brand plan
  const { mutateAsync: updateBrandPlan } = useUpdateBrandPlan(
    { id: brandPlan?.id ?? 0 },
    {
      onSuccess: () => {
        showToastMessage('Channel selection saved successfully')
        if (workspace) {
          WorkspaceClient.completeSetFreemiumBrandPlan(workspace.id)
        }
        closeAllModals()
      },
    },
  )
  const { mutateAsync: updateBrandPlanSchedule } = useUpdateBrandPlanSchedule({ id: plannedSchedule?.id ?? 0 })

  const handleSubmit = async (values: FormValues) => {
    const payload: BrandPlanContents = buildLimitedChannels(values)
    updateBrandPlanSchedule(payload)
    await updateBrandPlan(payload)
  }

  const handleClose = () => {
    trackFreemiumModalCompleted('cancel')
    closeModal()
  }

  const handleUpgrade = () => {
    trackFreemiumModalCompleted('upgrade')
    openVirtualMarketerPlansModal()
  }

  const handleContinue = () => {
    trackFreemiumModalCompleted('continue')
    openModal(AutopilotFreemiumChannelSelectModal, { onSubmit: handleSubmit })
  }

  const handleDismiss = () => {
    trackFreemiumModalCompleted(null)
    closeModal()
  }

  return (
    <Modal.Root onPressOutside={handleDismiss} bodyClassName={Styles.bodyClassName} size="lg">
      <Modal.Content className={Styles.modalContent}>
        <div className={Styles.content}>
          <div className={Styles.leftPanel}>
            <div className={Styles.leftPanelContent}>
              <div className={Styles.header}>
                <Heading level={1}>
                  Your trial has ended. Reactivate now to resume publishing your full schedule.
                </Heading>
                <Text>
                  You're now on the <strong>Free</strong> plan—your full schedules are limited to{' '}
                  <strong>3 channels</strong>. Upgrade to resume publishing on all channels.
                </Text>
              </div>

              <div className={Styles.freePlanSection}>
                <div className={Styles.freePlanSectionLeft}>
                  <Heading level={5}>Free Plan (kept active):</Heading>
                  <Text>
                    <ul>
                      <li>{FREE_REWARD_TIERS[0]} posts/week per channel (up to 3 channels)</li>
                      <li>Connect up to 3 channels</li>
                      <li>Posts include the Blaze watermark</li>
                    </ul>
                  </Text>
                </div>
                <div></div>
              </div>

              {isFreemiumReferrals && (
                <>
                  <div className={Styles.referralSection}>
                    <Heading level={5}>Earn higher weekly limits with referrals</Heading>
                    <div>
                      <Button
                        frontIcon={Gift01}
                        variant="tertiary"
                        endIcon={ArrowRight}
                        onPress={() => openModal(ReferralModalV2)}
                        aria-label="See How Referrals Work"
                      >
                        See How Referrals Work
                      </Button>
                    </div>
                  </div>
                  <div className={Styles.referralInput}>
                    <div className={Styles.shareButtonsContainer}>
                      <div className={Styles.urlContainer}>{referralUrl ? referralUrl : '...'}</div>
                      <Button
                        frontIcon={Copy}
                        onPress={copyUrlToClipboard}
                        isDisabled={!referralUrl}
                        variant="secondary"
                        aria-label="Copy"
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                </>
              )}

              <div className={Styles.proSection}>
                <Heading level={5}>Upgrade to unlock:</Heading>
                <div className={Styles.proSectionHeading}>
                  <Send1 size={24} color="#9500FF" /> <Heading level={4}>Unlimited posting & cross-posting</Heading>
                </div>
                <div className={Styles.proSectionHeading}>
                  <XSquareContained size={24} color="#9500FF" /> <Heading level={4}>No Blaze watermark</Heading>
                </div>
                <div className={Styles.proSectionHeading}>
                  <Help1 size={24} color="#9500FF" /> <Heading level={4}>24/7 live support</Heading>
                </div>
              </div>
            </div>
            <div className={Styles.buttonContainer}>
              <Button variant="tertiary" size="lg" onPress={handleClose} aria-label="Close">
                Close
              </Button>
              <div className={Styles.inline}>
                <Button
                  size="lg"
                  frontIcon={LockOpen3}
                  onPress={handleUpgrade}
                  color="#6A00FF"
                  className={Styles.upgradeButton}
                  aria-label="Upgrade"
                >
                  Upgrade
                </Button>

                <Button
                  size="lg"
                  onPress={handleContinue}
                  className={Styles.selectChannelsButton}
                  aria-label="Continue with Free Plan"
                >
                  Continue with Free Plan
                </Button>
              </div>
            </div>
          </div>

          <div className={Styles.rightPanel}>
            <img
              src="https://res.cloudinary.com/almanac/image/upload/v1758754333/blaze_assets/autopilot_upgrade_modal/freemium_upgrade_right_l1rchl.png"
              alt="Campaign Dashboard"
              className={Styles.backgroundImage}
            />
          </div>
        </div>
      </Modal.Content>
    </Modal.Root>
  )
}
